import { ApolloClient, DefaultContext } from '@apollo/client'
import { bind } from '@react-rxjs/core'
import { createSignal } from '@react-rxjs/utils'
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  from,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs'

import {
  ApplyCustomerStoreCreditDocument,
  ApplyCustomerStoreCreditMutationVariables,
  GetCustomerStoreCreditDocument,
  UpdateCustomerStoreCreditDocument,
  UpdateCustomerStoreCreditMutation,
  UpdateCustomerStoreCreditMutationVariables,
} from '../../../graphql/magento'
import {
  CustomerAvailableBalanceDocument,
  CustomerAvailableBalanceQuery,
} from '../../../graphql/search'
import { isNotNull } from '../../../utils/collectionTools'
import { constVoid } from '../../../utils/constant'
import { left, LeftResult, right, RightResult } from '../../../utils/result'
import { coerceCaughtToLeft } from '../../../utils/rx/errors'
import { fromObservableQuery } from '../../../utils/rx/observable-query'
import { mapRight, switchMapRight, tapRight } from '../../../utils/rx/operators'
import { Maybe } from '../../../utils/types'
import { latestAccessToken$ } from '../../auth/state'
import { latestApolloClient$, latestCommaClient$ } from '../../graphql/apollo'
import { latestCustomerCart$, refetchCustomerCartWithInterval$ } from './cart'

const [applyRewardsInput$, setApplyRewardsInput] = createSignal<number>()

export { setApplyRewardsInput }

const watchAvailableBalanceFactory = (client: ApolloClient<unknown>, token: string) =>
  fromObservableQuery(
    client.watchQuery({
      query: CustomerAvailableBalanceDocument,
      context: { token } as DefaultContext,
      errorPolicy: 'none',
      fetchPolicy: 'cache-and-network',
    }),
  ).pipe(
    // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server
    map(({ data }) => ({ _tag: 'Right' as const, data })),
    catchError(coerceCaughtToLeft),
  )

const getCustomerStoreCreditFactory = (client: ApolloClient<unknown>, token: string) =>
  from(
    client.query({
      query: GetCustomerStoreCreditDocument,
      context: { token } as DefaultContext,
      errorPolicy: 'none',
    }),
  ).pipe(
    // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server
    map(({ data }) => ({ _tag: 'Right' as const, data })),
    catchError(coerceCaughtToLeft),
  )

const updateCustomerStoreCreditFactory = (
  client: ApolloClient<unknown>,
  token: string,
  variables: UpdateCustomerStoreCreditMutationVariables,
) =>
  from(
    client.mutate({
      mutation: UpdateCustomerStoreCreditDocument,
      variables,
      context: { token },
      errorPolicy: 'none',
    }),
  ).pipe(
    // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server
    map(({ data }) => ({ _tag: 'Right' as const, data })),
    tap(() => {
      // manually update cache after successful mutation
      const query = GetCustomerStoreCreditDocument
      const cachedQuery = client.cache.readQuery({ query })
      client.cache.writeQuery({
        query,
        data: {
          customer: {
            ...cachedQuery?.customer,
            mp_store_credit: {
              ...cachedQuery?.customer?.mp_store_credit,
              mp_credit_balance: variables.amount,
            },
          },
        },
      })
    }),
    catchError(coerceCaughtToLeft),
  )

const applyCustomerStoreCreditFactory = (
  client: ApolloClient<unknown>,
  token: string,
  variables: ApplyCustomerStoreCreditMutationVariables,
) =>
  from(
    client.mutate({
      mutation: ApplyCustomerStoreCreditDocument,
      variables,
      context: { token },
      errorPolicy: 'none',
    }),
  ).pipe(
    // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server
    map(({ data }) => right(data)),
    catchError(coerceCaughtToLeft),
    switchMapRight(({ data }) =>
      refetchCustomerCartWithInterval$(0).pipe(
        mapRight(() => right(data)),
        take(1),
      ),
    ),
  )

export interface ApplyRewardsState {
  loading: boolean
  result:
    | { _tag: 'Left'; error: Error }
    | { _tag: 'Right'; data: UpdateCustomerStoreCreditMutation | null | undefined }
}

const parseMoney = (decimal: string): number | undefined => {
  const amount = Number(decimal)
  return Number.isNaN(amount) ? undefined : amount
}

type AvailableBalance = NonNullable<
  NonNullable<CustomerAvailableBalanceQuery['customer']>['availableBalance']
>

export type CustomerAvailableBalance = Maybe<
  AvailableBalance & {
    commissionsBalance: AvailableBalance['commissionsBalance'] & { parsed: number | undefined }
    totalBalance: AvailableBalance['totalBalance'] & { parsed: number | undefined }
  }
>

export const [useCustomerAvailableBalance, latestAvailableBalance$] = bind<
  LeftResult<Error> | RightResult<CustomerAvailableBalance>
>(
  combineLatest([
    latestApolloClient$,
    latestCommaClient$,
    latestAccessToken$.pipe(filter(isNotNull)),
  ]).pipe(
    switchMap(([defaultClient, commaClient, token]) =>
      watchAvailableBalanceFactory(commaClient, token).pipe(
        mapRight(({ data }): RightResult<CustomerAvailableBalance> => {
          const availableBalance = data.customer?.availableBalance
          if (!availableBalance) {
            return right(availableBalance)
          }
          const { commissionsBalance, totalBalance } = availableBalance
          return right(
            availableBalance && {
              ...availableBalance,
              commissionsBalance: {
                ...commissionsBalance,
                // parse money amount for calculation
                parsed:
                  commissionsBalance?.amount != null
                    ? parseMoney(commissionsBalance.amount)
                    : undefined,
              },
              totalBalance: {
                ...totalBalance,
                // parse money amount for calculation
                parsed: totalBalance?.amount != null ? parseMoney(totalBalance.amount) : undefined,
              },
            },
          )
        }),
        switchMapRight(({ data: availableBalance }) =>
          // compare total balance against M2 customer store credit and update M2 as needed
          getCustomerStoreCreditFactory(defaultClient, token).pipe(
            switchMapRight(({ data: customerData }) => {
              const m2CreditBalance = customerData?.customer?.mp_store_credit?.mp_credit_balance
              const totalBalance = availableBalance?.totalBalance?.parsed
              return totalBalance != null && totalBalance > 0 && m2CreditBalance !== totalBalance
                ? updateCustomerStoreCreditFactory(defaultClient, token, {
                    amount: totalBalance,
                  }).pipe(mapRight(() => right(availableBalance)))
                : of(right(availableBalance))
            }),
          ),
        ),
      ),
    ),
  ),
)

const lastSubmitted$ = new BehaviorSubject<number | undefined>(undefined)

const applyRewards$: Observable<{
  loading: boolean
  result: LeftResult<Error> | RightResult<void>
}> = combineLatest([latestAvailableBalance$, latestCustomerCart$]).pipe(
  switchMap(([availableBalanceResult, cartResult]) => {
    if (availableBalanceResult._tag === 'Left') {
      return of({ loading: false, result: availableBalanceResult })
    }
    if (cartResult._tag === 'Left') {
      return of({ loading: false, result: cartResult })
    }
    const { data: availableBalance } = availableBalanceResult
    const { data: cart } = cartResult
    const totalBalance = availableBalance?.totalBalance?.parsed
    return applyRewardsInput$.pipe(
      // NOTE: we may want to manipulate the time or when we debounce depending on the UX
      // - Our intent is to avoid excessive requests to M2 when adjusting the rewards slider
      // debounceTime(1000),
      switchMap((input) =>
        totalBalance == null || Number.isNaN(totalBalance) || totalBalance < input
          ? of({
              loading: false,
              result: left(new Error('Applied rewards exceeds available balance')),
            })
          : lastSubmitted$.pipe(
              filter((lastSubmitted) => lastSubmitted == null || lastSubmitted !== input),
              map(() => input),
              switchMap(() =>
                // avoid unnecessary mutation on initial load
                cart.customerCart?.rebate?.value === input
                  ? of(right(constVoid()))
                  : combineLatest([
                      latestApolloClient$,
                      latestAccessToken$.pipe(filter(isNotNull)),
                    ]).pipe(
                      switchMap(([client, token]) =>
                        applyCustomerStoreCreditFactory(client, token, {
                          amount: input,
                          cartId: cart.customerCart.id,
                        }),
                      ),
                    ),
              ),
              tapRight(() => lastSubmitted$.next(input)),
              map((result) => ({
                loading: false,
                result: result._tag === 'Left' ? result : right(constVoid()),
              })),
              startWith({ loading: true, result: right(constVoid()) }),
            ),
      ),
      startWith({ loading: false, result: right(constVoid()) }),
    )
  }),
)

export const [useApplyRewards, applyRewardsState$] = bind(applyRewards$)
